/**
 * DailyChart Component
 *
 * One bar per day, stacked by series (usually platforms), on a single axis.
 * Every day in the period is present, so gaps read as zero delivery rather
 * than being squeezed out. Hover shows every series for that day; with more
 * than one series a legend names them, and a screen-reader table carries
 * the same numbers.
 *
 * Usage:
 *   <DailyChart
 *     data={[{ date: '2026-09-30', meta: 12.5, google: 4 }]}
 *     series={[{ key: 'meta', label: 'Meta', color: '#2a78d6' }]}
 *     formatValue={(v) => `$${v}`}
 *   />
 */

import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import { useTheme } from '@/hooks/useTheme';

export interface DailyChartSeries {
  key: string;
  label: string;
  color: string;
}

export interface DailyChartProps<Row extends { date: string }> {
  /** One row per day; each series reads its value from `row[series.key]`. */
  data: Row[];
  series: DailyChartSeries[];
  /** Full value format for the tooltip and table. */
  formatValue: (value: number) => string;
  /** Short format for axis ticks; defaults to formatValue. */
  formatTick?: (value: number) => string;
  height?: number;
  /** Accessible name for the chart. */
  label: string;
}

// Chart chrome per theme, matching the app's card and border tokens.
const CHROME = {
  light: { surface: '#ffffff', grid: '#e6e8f0', tick: '#666d7c', cursor: 'rgba(18,20,28,0.05)' },
  dark: { surface: '#1a1d26', grid: '#2a2f3d', tick: '#a7aebe', cursor: 'rgba(255,255,255,0.06)' },
};

function shortDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function longDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

export function DailyChart<Row extends { date: string }>({
  data,
  series,
  formatValue,
  formatTick,
  height = 260,
  label,
}: DailyChartProps<Row>) {
  const { theme } = useTheme();
  const chrome = CHROME[theme === 'dark' ? 'dark' : 'light'];
  const tick = formatTick ?? formatValue;
  const multi = series.length > 1;

  const renderTooltip = ({ active, payload, label: date }: TooltipProps<number, string>) => {
    if (!active || !payload?.length || typeof date !== 'string') return null;
    const total = payload.reduce((sum, p) => sum + (Number(p.value) || 0), 0);
    return (
      <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-lg text-[12px] min-w-[150px]">
        <p className="text-ink-3 mb-1.5">{longDate(date)}</p>
        {multi && (
          <p className="flex justify-between gap-4 mb-1">
            <span className="font-bold text-ink">{formatValue(total)}</span>
            <span className="text-ink-3">Total</span>
          </p>
        )}
        {[...series].reverse().map((s) => {
          const value = Number(payload.find((p) => p.dataKey === s.key)?.value) || 0;
          return (
            <p key={s.key} className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-[3px] rounded-full" style={{ background: s.color }} />
                <span className={multi ? 'font-semibold text-ink-2' : 'font-bold text-ink'}>{formatValue(value)}</span>
              </span>
              <span className="text-ink-3">{s.label}</span>
            </p>
          );
        })}
      </div>
    );
  };

  return (
    // relative: keeps the absolutely-positioned sr-only table inside the chart
    // instead of escaping the page's scroll container.
    <div className="relative">
      {multi && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-[12px] text-ink-2" aria-label="Legend">
          {series.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-[3px]" style={{ background: s.color }} />
              {s.label}
            </li>
          ))}
        </ul>
      )}

      <div style={{ height }} role="img" aria-label={label}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} barCategoryGap="18%">
            <CartesianGrid vertical={false} stroke={chrome.grid} />
            <XAxis
              dataKey="date"
              tickFormatter={shortDate}
              tick={{ fill: chrome.tick, fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: chrome.grid }}
              minTickGap={28}
              interval="preserveStartEnd"
            />
            <YAxis
              tickFormatter={tick}
              tick={{ fill: chrome.tick, fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={64}
              tickCount={5}
              allowDecimals={false}
            />
            <Tooltip content={renderTooltip} cursor={{ fill: chrome.cursor }} isAnimationActive={false} />
            {series.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                stackId="day"
                fill={s.color}
                maxBarSize={24}
                // Surface-coloured edge = the 2px gap between stacked segments.
                stroke={chrome.surface}
                strokeWidth={multi ? 1 : 0}
                radius={i === series.length - 1 ? [4, 4, 0, 0] : 0}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* sr-only on a wrapper: tables ignore its 1px height and would stretch the page. */}
      <div className="sr-only">
      <table>
        <caption>{label}</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            {series.map((s) => <th key={s.key} scope="col">{s.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.date}>
              <th scope="row">{longDate(row.date)}</th>
              {series.map((s) => (
                <td key={s.key}>{formatValue(Number((row as Record<string, unknown>)[s.key]) || 0)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

export default DailyChart;
